import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { intentEmbeddingTimingUsesIntent30 } from '../utils/intentPipelineBadge';
import { intentEmbeddingDetailSeparatesPatientMessageToGenerationChip } from '../utils/chatTimings';

function formatMs(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  return `${value}ms`;
}

function TimingRow({ label, value, suffix = '', awaited = true, crossOut = false }) {
  const showCrossOut = crossOut || (awaited === false && value != null);
  return (
    <Typography
      variant="caption"
      component="div"
      color="text.secondary"
      sx={{
        lineHeight: 1.55,
        textDecoration: showCrossOut ? 'line-through' : undefined,
        opacity: showCrossOut ? 0.62 : 1,
      }}
    >
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}
      </Box>
      {': '}
      {value ?? 'N/A'}
      {suffix}
    </Typography>
  );
}

function MedicalHistoryProbabilityBars({ probabilities, selected, labelWidth = 160 }) {
  const chosen = new Set(Array.isArray(selected) ? selected : []);
  const rows = Object.entries(probabilities && typeof probabilities === 'object' ? probabilities : {})
    .map(([label, raw]) => ({ label, value: Number(raw) }))
    .filter((row) => row.label && Number.isFinite(row.value))
    .sort((a, b) => b.value - a.value);
  if (rows.length === 0) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      {rows.map((row) => {
        const pct = Math.round(row.value * 1000) / 10;
        const picked = chosen.has(row.label);
        return (
          <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ width: labelWidth, flexShrink: 0, fontWeight: picked ? 700 : 400, lineHeight: 1.3 }}
            >
              {row.label}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, pct))}
              color={picked ? 'primary' : 'inherit'}
              aria-label={`${row.label} probability ${pct}%`}
              sx={{ flex: 1, height: 6, borderRadius: 3, opacity: picked ? 1 : 0.45 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ width: 44, textAlign: 'right', flexShrink: 0 }}>
              {`${pct}%`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function formatClarifyPct(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  return `${Math.round(value * 1000) / 10}%`;
}

function JevPostIntentScores({ detail }) {
  const segments = Array.isArray(detail?.jevPostIntent?.segments) ? detail.jevPostIntent.segments : [];
  if (segments.length === 0) return null;
  return (
    <Box sx={{ mt: 0.35, mb: 0.35 }}>
      {segments.map((segment) => {
        const clarifyPct = formatClarifyPct(segment.clarify);
        const confidence = typeof segment.confidence === 'number' ? segment.confidence.toFixed(2) : null;
        const title = segments.length > 1 ? `Disambiguation ${segment.id}` : 'Disambiguation';
        return (
          <Box key={segment.id ?? segment.choice} sx={{ mt: 0.5 }}>
            <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {title}
              </Box>
              {`: ${segment.choice || 'None'}`}
              {confidence != null ? ` (confidence ${confidence})` : ''}
              {clarifyPct != null
                ? ` · clarify ${clarifyPct}${segment.needs_clarification ? ' (asking)' : ''}`
                : ''}
            </Typography>
            <MedicalHistoryProbabilityBars
              probabilities={segment.probabilities}
              selected={segment.choice ? [segment.choice] : []}
              labelWidth={220}
            />
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Intent 2.0 / 3.0 expandable timing under chat bubbles — same phases as the admin embedding test bed,
 * fixed order, with explicit N/A when a phase did not apply.
 * Phases that ran without blocking the reply are shown struck through.
 */
export default function IntentEmbeddingTimingBreakdown({ detail, requestDurationMs = null }) {
  if (!detail || typeof detail !== 'object') return null;
  const intent30 = intentEmbeddingTimingUsesIntent30(detail);

  const phaseAwaited = detail.phaseAwaited && typeof detail.phaseAwaited === 'object' ? detail.phaseAwaited : {};
  const segMethod = detail.segmentationMethod === 'llm' ? 'llm' : 'classic';
  const segMs = formatMs(detail.segmentationMs);
  const embedMs = formatMs(detail.embeddingMs);
  const retrievalMs = formatMs(detail.retrievalMs);
  const lwMs = formatMs(detail.lightweightRoutingDurationMs);
  const mhExtractMs = formatMs(detail.medical_history_extraction?.duration_ms);
  const postIntentMs = formatMs(detail.llmPostIntentDisambiguationMs);
  const auditMs = formatMs(detail.interrogativeIntentAuditMs ?? detail.interrogativeIntentAudit?.duration_ms);
  const auditLabel =
    detail.interrogativeIntentAudit?.source === 'jev' ||
    detail.interrogative_intent_audit?.source === 'jev'
      ? 'Interrogative audit (TypeSafe)'
      : detail.interrogativeIntentAudit?.source === 'heuristic' ||
          detail.interrogative_intent_audit?.source === 'heuristic'
        ? 'Interrogative audit (heuristic)'
        : 'Interrogative audit LLM';
  const patientMs = formatMs(detail.patientPrompt?.duration_ms);
  const enrichMs = formatMs(detail.enrichmentAfterMatchMs);
  const pipelinePrepMs = formatMs(detail.pipelinePrepRemainderMs);
  const routeProcessingMs = formatMs(detail.routeProcessingRemainderMs);
  const totalMs = formatMs(detail.total);
  const requestMs =
    typeof requestDurationMs === 'number' && !Number.isNaN(requestDurationMs) && requestDurationMs > 0
      ? `${Math.round(requestDurationMs)}ms`
      : null;
  const classifier = detail.classifierInference;
  const mhAbandoned = detail.medical_history_extraction?.abandoned === true;
  const patientPromptUsed = detail.patientPromptUsedForResponse === true;
  const patientPromptOnGenerationChip = intentEmbeddingDetailSeparatesPatientMessageToGenerationChip(detail);
  const hasCrossedOutPhase =
    (lwMs != null && phaseAwaited.lightweightRouting === false) ||
    (mhExtractMs != null && (phaseAwaited.medicalHistoryExtraction === false || mhAbandoned)) ||
    (postIntentMs != null && phaseAwaited.postIntentLlm === false) ||
    (auditMs != null && phaseAwaited.interrogativeAudit === false) ||
    (!patientPromptOnGenerationChip &&
      patientMs != null &&
      (phaseAwaited.patientPrompt === false || !patientPromptUsed));

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        borderRadius: 1,
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4, display: 'block', mb: 0.75 }}>
        {intent30 ? 'Intent 3.0 timing' : 'Intent 2.0 timing'}
      </Typography>
      <TimingRow label="Segmentation" value={segMs != null ? `${segMs} (${segMethod})` : null} awaited={phaseAwaited.segmentation !== false} />
      <TimingRow label="Embed" value={embedMs} awaited={phaseAwaited.embedding !== false} />
      <TimingRow label="Retrieval" value={retrievalMs} awaited={phaseAwaited.retrieval !== false} />
      <TimingRow label="Lightweight Fallback" value={lwMs} awaited={phaseAwaited.lightweightRouting !== false} />
      <TimingRow
        label="Medical History Intent LLM"
        value={mhExtractMs}
        suffix={mhAbandoned ? ' (no topics; not used for routing)' : ''}
        awaited={phaseAwaited.medicalHistoryExtraction !== false}
        crossOut={mhAbandoned}
      />
      <TimingRow
        label={detail.postIntentDisambiguationSource === 'jev' ? 'Post-intent (TypeSafe)' : 'Post-intent LLM'}
        value={postIntentMs}
        awaited={phaseAwaited.postIntentLlm !== false}
      />
      <JevPostIntentScores detail={detail} />
      <TimingRow label={auditLabel} value={auditMs} awaited={phaseAwaited.interrogativeAudit !== false} />
      {!patientPromptOnGenerationChip ? (
        <TimingRow
          label="Patient LLM Message"
          value={patientMs}
          awaited={phaseAwaited.patientPrompt !== false && patientPromptUsed}
          crossOut={patientMs != null && !patientPromptUsed}
        />
      ) : null}
      {pipelinePrepMs != null ? (
        <TimingRow
          label="Intent index prep"
          value={pipelinePrepMs}
          suffix=" (embedding table coverage, etc.)"
          awaited
        />
      ) : null}
      {routeProcessingMs != null ? (
        <TimingRow
          label="Route processing"
          value={routeProcessingMs}
          suffix=" (session load, gates, postprocess, etc.)"
          awaited
        />
      ) : null}
      <TimingRow label="Enrichment" value={enrichMs} awaited={phaseAwaited.enrichment !== false} />
      {classifier?.path === 'jev' ? (
        <Box sx={{ mt: 0.35 }}>
          <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              Medical history
            </Box>
            {`: ${classifier.timings?.totalMs != null ? `${classifier.timings.totalMs}ms` : 'N/A'}${classifier.modelName ? ` · ${classifier.modelName}` : ''}`}
          </Typography>
          <MedicalHistoryProbabilityBars probabilities={classifier.probabilities} selected={classifier.topics} />
        </Box>
      ) : null}
      {requestMs != null ? (
        <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.55, mt: 0.35 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Request wait (server)
          </Box>
          {': '}
          {requestMs}
          {' — matches the Total chip (request in → response out).'}
        </Typography>
      ) : null}
      <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.55, mt: 0.35 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          Pipeline wall
        </Box>
        {': '}
        {totalMs ?? 'N/A'}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}>
        Phase rows are processing wall times inside the intent pipeline. They may overlap; the Intent chip is the remainder
        after Translation, Target, and Generation so the four chips sum to request wait.
        {hasCrossedOutPhase ? ' Crossed-out phases ran but did not block the reply.' : ''}
      </Typography>
    </Box>
  );
}
